/**
 * Utility functions for ContinuousDocument component
 * 
 * This module contains helper functions for content normalization and validation.
 */

// Reason: Define which parent types can contain inline images
const INLINE_IMAGE_PARENT_TYPES = new Set(['paragraph', 'heading', 'tableCell', 'tableHeader']);

/**
 * Normalizes resizableImage nodes to ensure they're properly wrapped in paragraphs
 * 
 * Args:
 *   node (object): The node to normalize
 *   parentType (string|null): The type of the parent node
 * 
 * Returns:
 *   object: The normalized node
 */
export const normalizeResizableImageNode = (node, parentType = null) => {
  if (!node || typeof node !== 'object') {
    return node;
  }

  const normalizedNode = { ...node };

  if (Array.isArray(node.content)) {
    normalizedNode.content = node.content
      .map((child) => normalizeResizableImageNode(child, node.type))
      .filter(Boolean);
  }

  if (
    normalizedNode.type === 'resizableImage' &&
    parentType &&
    !INLINE_IMAGE_PARENT_TYPES.has(parentType)
  ) {
    return {
      type: 'paragraph',
      attrs: normalizedNode.attrs?.textAlign
        ? { textAlign: normalizedNode.attrs.textAlign }
        : undefined,
      content: [normalizedNode],
    };
  }

  if (normalizedNode.type === 'resizableImage' && !parentType) {
    return {
      type: 'paragraph',
      attrs: normalizedNode.attrs?.textAlign
        ? { textAlign: normalizedNode.attrs.textAlign }
        : undefined,
      content: [normalizedNode],
    };
  }

  return normalizedNode;
};

/**
 * Normalizes document content structure to ensure proper nesting
 * 
 * Args:
 *   value (object|string): The content to normalize
 * 
 * Returns:
 *   object|string: The normalized content
 */
export const normalizeDocumentContent = (value) => {
  if (!value || typeof value === 'string') {
    return value;
  }

  try {
    const cloned = JSON.parse(JSON.stringify(value));

    if (cloned.type === 'doc' && Array.isArray(cloned.content)) {
      cloned.content = cloned.content
        .map((child) => normalizeResizableImageNode(child, 'doc'))
        .filter(Boolean);
    }

    return cloned;
  } catch (error) {
    console.error('Error normalizing content structure:', error);
    return value;
  }
};

/**
 * True when the pasted `text/html` is ProseMirror's OWN internal clipboard
 * payload (a copy made from inside the editor — including a table cell-range
 * copy, which serializes as `<table>` markup).
 *
 * Reason (T-017 / Complaint B): the paste handler intercepts any HTML that
 * contains a `<table>` to offer the "convert to table" dialog. But ProseMirror's
 * clipboard HTML for an internal cell-range copy ALSO contains a `<table>`, so
 * that interception hijacks intra-editor table copy/paste, routing it through
 * the sanitizer and losing native slice semantics. ProseMirror marks its own
 * clipboard output with a `data-pm-slice` attribute (see prosemirror-view
 * serializeForClipboard) — when present, let ProseMirror handle the paste
 * natively.
 *
 * @param {string} html - clipboard `text/html`
 * @returns {boolean}
 */
export const isInternalProseMirrorPaste = (html) =>
  typeof html === 'string' && /data-pm-slice/i.test(html);

/**
 * Firma de una seleccion, para que `onSelectionUpdate` solo notifique cuando la
 * seleccion REALMENTE cambio (#13).
 *
 * INVARIANTE: la firma del caso "sin seleccion" es un string NO vacio y estable.
 * Si se compara contra `''` (o se resetea el ref a `''`), la guardia nunca corta
 * y el editor dispara un re-render del padre por cada tecla.
 *
 * @returns {string}
 */
export const selectionSignature = ({ trimmedText = '', images = [], hasTables = false, tableGfm = null } = {}) =>
  `${trimmedText}|${images.length}|${images.map((img) => img.src).join(',')}|${hasTables}|${tableGfm ? tableGfm.length : 0}`;

/**
 * Unwraps code fence from markdown text
 * 
 * Args:
 *   s (string): The string to unwrap
 * 
 * Returns:
 *   string: The unwrapped string
 */
export const unwrapCodeFence = (s) => {
  if (!s) return '';
  const m = s.match(/^```[a-zA-Z]*\s*([\s\S]*?)\s*```$/m);
  return m ? m[1].trim() : s;
};

/**
 * Checks if text looks like a Markdown table
 * 
 * Args:
 *   text (string): The text to check
 * 
 * Returns:
 *   boolean: True if text looks like a Markdown table
 */
export const looksLikeMarkdownTable = (text) => {
  // Reason: tolerate blank lines between header and separator and trim whitespace
  const lines = (text || '')
    .replace(/\r/g, '')
    .split('\n')
    .map(l => l.trim())
    .filter(l => l.length > 0);
  if (lines.length < 2) return false;
  if (!/\|/.test(lines[0])) return false;
  const separator = lines[1];
  // Reason: accept GFM separators with 2+ dashes and optional alignment colons.
  if (!/^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?$/.test(separator)) return false;
  return true;
};

/**
 * Converts Markdown table to TipTap JSON format
 * 
 * Args:
 *   md (string): The Markdown table string
 * 
 * Returns:
 *   object: TipTap JSON table structure
 */
export const mdTableToJSON = (md) => {
  // Reason: Convert markdown table to TipTap JSON format for proper rendering
  const rows = md.trim().split('\n').filter(Boolean);
  const cells = (line) => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
  const head = cells(rows[0]);
  const bodyRows = rows.slice(2).map(r => cells(r));

  // Build table rows in TipTap JSON format
  const headerRow = {
    type: 'tableRow',
    content: head.map(cell => ({
      type: 'tableHeader',
      content: [{ type: 'paragraph', content: [{ type: 'text', text: cell }] }]
    }))
  };

  const bodyRowsJSON = bodyRows.map(row => ({
    type: 'tableRow',
    content: row.map(cell => ({
      type: 'tableCell',
      content: [{ type: 'paragraph', content: [{ type: 'text', text: cell }] }]
    }))
  }));

  return {
    type: 'table',
    content: [headerRow, ...bodyRowsJSON]
  };
};

